import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Card,
  Alert,
  Badge,
  ListGroup,
  Spinner
} from 'react-bootstrap';
import {
  FaPlus,
  FaTrash,
  FaSave,
  FaTimes,
  FaInfoCircle
} from 'react-icons/fa';
import ObjectConfigApi from '../../../api/ObjectConfigApi';
import PicklistApi from '../../../api/PicklistApi';
import DynamicFieldInput from '../../common/DynamicFieldInput';

const ScoringRuleForm = ({ show, onHide, onSave, editingRule, objects, existingRules = [] }) => {
  const [formData, setFormData] = useState({
    object_name: '',
    description: '',
    is_active: true,
    logic: {
      logicType: 'AND',
      conditions: []
    },
    scoreStorage: {
      totalPositiveField: '',
      totalNegativeField: '',
      totalScoreField: ''
    },
    rangeBasedUpdates: [] // Fields with score range mappings
  });

  const [fields, setFields] = useState([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const allOperators = {
    text: [
      { value: 'equals', label: 'Equals' },
      { value: 'not_equals', label: 'Not Equals' },
      { value: 'contains', label: 'Contains' },
      { value: 'not_contains', label: 'Does Not Contain' },
      { value: 'starts_with', label: 'Starts With' },
      { value: 'ends_with', label: 'Ends With' },
      { value: 'is_empty', label: 'Is Empty' },
      { value: 'is_not_empty', label: 'Is Not Empty' }
    ],
    number: [
      { value: 'equals', label: 'Equals' },
      { value: 'not_equals', label: 'Not Equals' },
      { value: 'greater_than', label: 'Greater Than' },
      { value: 'greater_than_equals', label: 'Greater Than or Equal' },
      { value: 'less_than', label: 'Less Than' },
      { value: 'less_than_equals', label: 'Less Than or Equal' },
      { value: 'is_empty', label: 'Is Empty' },
      { value: 'is_not_empty', label: 'Is Not Empty' }
    ],
    boolean: [
      { value: 'is_true', label: 'Is True' },
      { value: 'is_false', label: 'Is False' }
    ],
    picklist: [
      { value: 'equals', label: 'Equals' },
      { value: 'not_equals', label: 'Not Equals' },
      { value: 'is_empty', label: 'Is Empty' },
      { value: 'is_not_empty', label: 'Is Not Empty' }
    ],
    date: [
      { value: 'equals', label: 'Equals' },
      { value: 'not_equals', label: 'Not Equals' },
      { value: 'greater_than', label: 'After' },
      { value: 'greater_than_equals', label: 'On or After' },
      { value: 'less_than', label: 'Before' },
      { value: 'less_than_equals', label: 'On or Before' },
      { value: 'is_empty', label: 'Is Empty' },
      { value: 'is_not_empty', label: 'Is Not Empty' }
    ],
    lookup: [
      { value: 'equals', label: 'Equals' },
      { value: 'not_equals', label: 'Not Equals' },
      { value: 'is_empty', label: 'Is Empty' },
      { value: 'is_not_empty', label: 'Is Not Empty' }
    ]
  };

  // Load fields for selected object
  useEffect(() => {
    if (formData.object_name && show) {
      fetchObjectFields();
    }
  }, [formData.object_name, show]);

  // Clear error when modal opens/closes
  useEffect(() => {
    if (!show) {
      setError('');
      setSaving(false);
    }
  }, [show]);

  // Populate form when editing
  useEffect(() => {
    if (editingRule) {
      console.log('=== EDITING RULE ===');
      console.log('Full editing rule:', editingRule);
      console.log('Config raw:', editingRule.config);
      console.log('Config type:', typeof editingRule.config);

      // Parse config from editingRule
      let config = editingRule.config;
      if (typeof config === 'string') {
        try {
          config = JSON.parse(config);
          console.log('Parsed config from string:', config);
        } catch (e) {
          console.error('Error parsing config:', e);
          config = {};
        }
      } else {
        console.log('Config is already object:', config);
      }

      // Handle double-nested config (config.config instead of config)
      if (config.config) {
        console.log('Detected double-nested config, unwrapping...');
        config = config.config;
      }

      console.log('Final config.logic:', config.logic);
      console.log('Final config.scoreStorage:', config.scoreStorage);
      console.log('Final config.rangeBasedUpdates:', config.rangeBasedUpdates);

      const formDataToSet = {
        object_name: editingRule.object_name || '',
        description: editingRule.description || '',
        is_active: editingRule.is_active !== false,
        logic: config.logic || { logicType: 'AND', conditions: [] },
        scoreStorage: config.scoreStorage || {
          totalPositiveField: '',
          totalNegativeField: '',
          totalScoreField: ''
        },
        rangeBasedUpdates: config.rangeBasedUpdates || []
      };

      console.log('Setting form data to:', formDataToSet);
      setFormData(formDataToSet);
    } else {
      // Reset form for new rule
      setFormData({
        object_name: '',
        description: '',
        is_active: true,
        logic: {
          logicType: 'AND',
          conditions: []
        },
        scoreStorage: {
          totalPositiveField: '',
          totalNegativeField: '',
          totalScoreField: ''
        },
        rangeBasedUpdates: []
      });
    }
  }, [editingRule, show]);

  const fetchObjectFields = async () => {
    if (!formData.object_name) {
      console.log('No object name, skipping field fetch');
      return;
    }

    setLoadingFields(true);
    setFields([]); // Clear fields when loading
    try {
      console.log('Fetching fields for object:', formData.object_name);
      const response = await ObjectConfigApi.fetchConfigObjectByNameWithFields(formData.object_name);
      console.log('Fields response:', response);

      if (response?.success && response.data) {
        const fieldList = response.data.fields || [];
        console.log('Raw field list:', fieldList);
        const filteredFields = fieldList.filter(f => !f.is_deleted);
        console.log('Filtered fields:', filteredFields);

        // Fetch picklist values for picklist fields
        const picklistApi = new PicklistApi();
        const fieldsWithPicklists = await Promise.all(
          filteredFields.map(async (field) => {
            if (field.data_type === 'picklist' || field.data_type === 'multipicklist') {
              try {
                const picklistResponse = await picklistApi.getActivePicklistValues(field.id);
                if (picklistResponse?.data?.success) {
                  return {
                    ...field,
                    picklist_values: picklistResponse.data.data || []
                  };
                }
              } catch (err) {
                console.error(`Error fetching picklist values for ${field.field_name}:`, err);
              }
            }
            return field;
          })
        );

        setFields(fieldsWithPicklists);
        console.log('Fields with picklist values:', fieldsWithPicklists);
      } else {
        console.error('Invalid response structure:', response);
        setError('Failed to fetch object fields - invalid response');
      }
    } catch (err) {
      console.error('Error fetching fields:', err);
      setError('Failed to fetch object fields: ' + err.message);
    } finally {
      setLoadingFields(false);
    }
  };

  const handleAddCondition = () => {
    setFormData(prev => ({
      ...prev,
      logic: {
        ...prev.logic,
        conditions: [
          ...prev.logic.conditions,
          { field: '', operator: 'equals', value: '', scoreOperation: 'add', score: 0 }
        ]
      }
    }));
  };

  const handleRemoveCondition = (index) => {
    setFormData(prev => ({
      ...prev,
      logic: {
        ...prev.logic,
        conditions: prev.logic.conditions.filter((_, i) => i !== index)
      }
    }));
  };

  const handleConditionChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      logic: {
        ...prev.logic,
        conditions: prev.logic.conditions.map((cond, i) =>
          i === index ? { ...cond, [field]: value } : cond
        )
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      // Validation
      if (!formData.object_name) {
        setError('Object is required');
        setSaving(false);
        return;
      }

      if (formData.logic.conditions.length === 0) {
        setError('At least one condition is required');
        setSaving(false);
        return;
      }

      // Validate each condition
      for (let i = 0; i < formData.logic.conditions.length; i++) {
        const cond = formData.logic.conditions[i];
        if (!cond.field) {
          setError(`Condition ${i + 1}: Field is required`);
          setSaving(false);
          return;
        }
        if (!cond.operator) {
          setError(`Condition ${i + 1}: Operator is required`);
          setSaving(false);
          return;
        }
        // Value is not required for is_empty, is_not_empty, is_true, is_false operators
        if (!['is_empty', 'is_not_empty', 'is_true', 'is_false'].includes(cond.operator) && !cond.value && cond.value !== 0) {
          setError(`Condition ${i + 1}: Value is required`);
          setSaving(false);
          return;
        }
        // Validate score
        if (!cond.score || isNaN(cond.score)) {
          setError(`Condition ${i + 1}: Score must be a valid number`);
          setSaving(false);
          return;
        }
      }

      // Validate that at least one range-based field update exists
      if (formData.rangeBasedUpdates.length === 0) {
        setError('At least one field must be added in "Update Fields Based on Score Ranges" section');
        setSaving(false);
        return;
      }

      // Validate range-based updates
      for (let i = 0; i < formData.rangeBasedUpdates.length; i++) {
        const update = formData.rangeBasedUpdates[i];
        if (!update.field) {
          setError(`Field Update ${i + 1}: Field selection is required`);
          setSaving(false);
          return;
        }

        if (update.ranges.length === 0) {
          setError(`Field Update ${i + 1}: At least one score range is required`);
          setSaving(false);
          return;
        }

        for (let j = 0; j < update.ranges.length; j++) {
          const range = update.ranges[j];
          if (range.minScore === '' || range.minScore === null) {
            setError(`Field Update ${i + 1}, Range ${j + 1}: From Score is required`);
            setSaving(false);
            return;
          }
          if (range.maxScore === '' || range.maxScore === null) {
            setError(`Field Update ${i + 1}, Range ${j + 1}: To Score is required`);
            setSaving(false);
            return;
          }
          if (parseInt(range.minScore) > parseInt(range.maxScore)) {
            setError(`Field Update ${i + 1}, Range ${j + 1}: From Score (${range.minScore}) cannot be greater than To Score (${range.maxScore})`);
            setSaving(false);
            return;
          }
          if (!range.value) {
            setError(`Field Update ${i + 1}, Range ${j + 1}: Value is required`);
            setSaving(false);
            return;
          }
        }
      }

      // Build config object
      const config = {
        logic: formData.logic,
        scoreStorage: formData.scoreStorage,
        rangeBasedUpdates: formData.rangeBasedUpdates
      };

      // Submit with config
      const result = await onSave({
        object_name: formData.object_name,
        description: formData.description,
        is_active: formData.is_active,
        config: config
      }, setError);

      // If save failed, result will be { success: false }
      if (result && !result.success) {
        setSaving(false);
      }
    } catch (err) {
      console.error('Form submit error:', err);
      setError('An error occurred while saving');
      setSaving(false);
    }
  };

  const getFieldType = (fieldName) => {
    const field = fields.find(f => f.field_name === fieldName);
    return field?.data_type || 'text';
  };

  const needsValueInput = (operator) => {
    return !['is_empty', 'is_not_empty', 'is_true', 'is_false'].includes(operator);
  };

  const getFieldByName = (fieldName) => {
    return fields.find(f => f.field_name === fieldName);
  };

  const handleAddRangeBasedUpdate = () => {
    setFormData(prev => ({
      ...prev,
      rangeBasedUpdates: [
        ...prev.rangeBasedUpdates,
        { field: '', ranges: [] }
      ]
    }));
  };

  const handleRemoveRangeBasedUpdate = (index) => {
    setFormData(prev => ({
      ...prev,
      rangeBasedUpdates: prev.rangeBasedUpdates.filter((_, i) => i !== index)
    }));
  };

  const handleRangeBasedUpdateFieldChange = (index, fieldName) => {
    setFormData(prev => ({
      ...prev,
      rangeBasedUpdates: prev.rangeBasedUpdates.map((update, i) =>
        i === index ? { ...update, field: fieldName, ranges: [] } : update
      )
    }));
  };

  const handleAddRange = (updateIndex) => {
    setFormData(prev => {
      const currentUpdate = prev.rangeBasedUpdates[updateIndex];
      const lastRange = currentUpdate.ranges[currentUpdate.ranges.length - 1];

      // Auto-set minScore to last maxScore + 1
      const newMinScore = lastRange && lastRange.maxScore !== '' && lastRange.maxScore !== null
        ? parseInt(lastRange.maxScore) + 1
        : '';

      return {
        ...prev,
        rangeBasedUpdates: prev.rangeBasedUpdates.map((update, i) =>
          i === updateIndex
            ? {
              ...update,
              ranges: [
                ...update.ranges,
                { minScore: newMinScore, maxScore: '', value: '' }
              ]
            }
            : update
        )
      };
    });
  };

  const handleRemoveRange = (updateIndex, rangeIndex) => {
    setFormData(prev => ({
      ...prev,
      rangeBasedUpdates: prev.rangeBasedUpdates.map((update, i) =>
        i === updateIndex
          ? {
            ...update,
            ranges: update.ranges.filter((_, ri) => ri !== rangeIndex)
          }
          : update
      )
    }));
  };

  const handleRangeChange = (updateIndex, rangeIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      rangeBasedUpdates: prev.rangeBasedUpdates.map((update, i) =>
        i === updateIndex
          ? {
            ...update,
            ranges: update.ranges.map((range, ri) => {
              if (ri === rangeIndex) {
                // Update current range
                return { ...range, [field]: value };
              } else if (ri === rangeIndex + 1 && field === 'maxScore') {
                // Auto-update next range's minScore when current maxScore changes
                const newMinScore = value !== '' && value !== null ? parseInt(value) + 1 : '';
                return { ...range, minScore: newMinScore };
              }
              return range;
            })
          }
          : update
      )
    }));
  };

  const isPicklistField = (fieldName) => {
    const field = getFieldByName(fieldName);
    return field && (field.data_type === 'picklist' || field.data_type === 'multipicklist');
  };

  const getPicklistValues = (fieldName) => {
    const field = getFieldByName(fieldName);
    return field?.picklist_values || [];
  };

  const isObjectUsed = (objectName) => {
    if (!existingRules || existingRules.length === 0) return false;
    return existingRules.some(rule => rule.object_name === objectName);
  };

  const getOperatorsForField = (fieldName) => {
    if (!fieldName) return allOperators.text;

    const field = getFieldByName(fieldName);
    if (!field) return allOperators.text;

    const dataType = field.data_type?.toLowerCase();

    // Map data types to operator categories
    if (dataType === 'boolean' || dataType === 'checkbox') {
      return allOperators.boolean;
    }
    if (dataType === 'number' || dataType === 'integer' || dataType === 'currency' || dataType === 'percent' || dataType === 'rollupsummary') {
      return allOperators.number;
    }
    if (dataType === 'picklist' || dataType === 'multipicklist') {
      return allOperators.picklist;
    }
    if (dataType === 'date' || dataType === 'datetime') {
      return allOperators.date;
    }
    if (dataType === 'lookup' || dataType === 'reference' || field.is_reference) {
      return allOperators.lookup;
    }

    // Default to text operators
    return allOperators.text;
  };

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          {editingRule ? 'Edit Scoring Rule' : 'Create Scoring Rule'}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Basic Information */}
          <Card className="mb-3">
            <Card.Header>
              <strong className='text-white'>Basic Information</strong>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Object *</Form.Label>
                    <Form.Select
                      value={formData.object_name}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          object_name: e.target.value,
                          logic: {
                            ...prev.logic,
                            conditions: [] // Clear conditions when object changes
                          }
                        }));
                      }}
                      required
                      disabled={!!editingRule} // Disable when editing
                    >
                      <option value="">-- Select an Object --</option>
                      {objects.map(obj => (
                        <option
                          key={obj.name}
                          value={obj.name}
                          disabled={!editingRule && isObjectUsed(obj.name)}
                        >
                          {obj.label} {!editingRule && isObjectUsed(obj.name) ? '(Already has rule)' : ''}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      {editingRule
                        ? 'Object cannot be changed when editing'
                        : 'Note: Only one scoring rule allowed per object'}
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Check
                      type="checkbox"
                      label="Active (Rule will be applied)"
                      checked={formData.is_active}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        is_active: e.target.checked
                      }))}
                      className="mt-4"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="e.g., High-value technology lead with annual revenue over $1M"
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        description: e.target.value
                      }))}
                    />
                    <Form.Text className="text-muted">
                      Optional: A clear description of what this rule does
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

            </Card.Body>
          </Card>

          {/* Conditions */}
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <strong>Scoring Criteria *</strong>
              {formData.object_name && !loadingFields && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleAddCondition}
                  disabled={loadingFields || !formData.object_name}
                >
                  <FaPlus className="me-1" />
                  Add Condition
                </Button>
              )}
            </Card.Header>
            <Card.Body>
              {!formData.object_name ? (
                <div className="text-center py-4 text-muted">
                  <FaInfoCircle size={32} className="mb-2" />
                  <p>Please select an object first to add conditions.</p>
                </div>
              ) : loadingFields ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                  <span className="ms-2">Loading fields...</span>
                </div>
              ) : formData.logic.conditions.length === 0 ? (
                <div className="text-center py-5">
                  <FaInfoCircle size={48} className="mb-3 text-muted" />
                  <h6 className="text-muted">No conditions added yet</h6>
                  <p className="text-muted small">Click "Add Condition" button above to get started.</p>
                  <Button
                    variant="primary"
                    onClick={handleAddCondition}
                    className="mt-2"
                  >
                    <FaPlus className="me-1" />
                    Add First Condition
                  </Button>
                </div>
              ) : (
                <ListGroup>
                  {formData.logic.conditions.map((condition, index) => (
                    <ListGroup.Item key={index}>
                      <Row className="align-items-center mb-2">
                        <Col md={1}>
                          <Badge bg="secondary">#{index + 1}</Badge>
                        </Col>
                        <Col md={2}>
                          <Form.Label className="small mb-1">Field</Form.Label>
                          <Form.Select
                            size="sm"
                            value={condition.field}
                            onChange={(e) => {
                              const newField = e.target.value;
                              handleConditionChange(index, 'field', newField);
                              // Reset operator and value when field changes
                              const operators = getOperatorsForField(newField);
                              if (operators.length > 0) {
                                handleConditionChange(index, 'operator', operators[0].value);
                              }
                              handleConditionChange(index, 'value', '');
                            }}
                            required
                          >
                            <option value="">-- Select Field --</option>
                            {fields.length === 0 && !loadingFields && formData.object_name && (
                              <option disabled>No fields available</option>
                            )}
                            {fields.map(field => (
                              <option key={field.field_name} value={field.field_name}>
                                {field.field_label || field.field_name}
                              </option>
                            ))}
                          </Form.Select>
                        </Col>
                        <Col md={2}>
                          <Form.Label className="small mb-1">Operator</Form.Label>
                          <Form.Select
                            size="sm"
                            value={condition.operator}
                            onChange={(e) => handleConditionChange(index, 'operator', e.target.value)}
                            required
                          >
                            {getOperatorsForField(condition.field).map(op => (
                              <option key={op.value} value={op.value}>
                                {op.label}
                              </option>
                            ))}
                          </Form.Select>
                        </Col>
                        <Col md={3}>
                          <Form.Label className="small mb-1">Value</Form.Label>
                          {needsValueInput(condition.operator) ? (
                            <DynamicFieldInput
                              field={getFieldByName(condition.field)}
                              value={condition.value}
                              displayValue={condition.displayValue}
                              onChange={(value, displayValue) => {
                                // For lookup fields, both value and displayValue are passed
                                // For other fields, only value is passed
                                handleConditionChange(index, 'value', value);
                                if (displayValue !== undefined) {
                                  handleConditionChange(index, 'displayValue', displayValue);
                                }
                              }}
                              operator={condition.operator}
                              size="sm"
                              placeholder="Value"
                            />
                          ) : (
                            <Form.Control
                              size="sm"
                              type="text"
                              value="N/A"
                              disabled
                            />
                          )}
                        </Col>
                        <Col md={2}>
                          <Form.Label className="small mb-1">Operation</Form.Label>
                          <Form.Select
                            size="sm"
                            value={condition.scoreOperation || 'add'}
                            onChange={(e) => handleConditionChange(index, 'scoreOperation', e.target.value)}
                            required
                          >
                            <option value="add">Add (+)</option>
                            <option value="subtract">Subtract (-)</option>
                          </Form.Select>
                        </Col>
                        <Col md={1}>
                          <Form.Label className="small mb-1">Score</Form.Label>
                          <Form.Control
                            size="sm"
                            type="number"
                            value={condition.score || 0}
                            onChange={(e) => handleConditionChange(index, 'score', parseInt(e.target.value) || 0)}
                            required
                            min="0"
                            placeholder="0"
                          />
                        </Col>
                        <Col md={1}>
                          <Form.Label className="small mb-1">&nbsp;</Form.Label>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleRemoveCondition(index)}
                            className="w-100"
                          >
                            <FaTrash />
                          </Button>
                        </Col>
                      </Row>

                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Card.Body>
          </Card>

          {/* Score Storage Fields */}
          <Card className="mb-3">
            <Card.Header>
              <strong>Store Scores in Fields</strong>
              <small className="text-muted ms-2">(Optional - for storing numeric scores)</small>
            </Card.Header>
            <Card.Body>
              {!formData.object_name ? (
                <div className="text-center py-3 text-muted">
                  <small>Select an object first</small>
                </div>
              ) : (
                <Row>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="small">Total Positive</Form.Label>
                      <Form.Select
                        size="sm"
                        value={formData.scoreStorage.totalPositiveField}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          scoreStorage: { ...prev.scoreStorage, totalPositiveField: e.target.value }
                        }))}
                      >
                        <option value="">-- Select Field --</option>
                        {fields
                          .filter(f => ['number', 'integer', 'currency', 'percent'].includes(f.data_type?.toLowerCase()))
                          .map(field => (
                            <option key={field.field_name} value={field.field_name}>
                              {field.field_label || field.field_name}
                            </option>
                          ))}
                      </Form.Select>
                      <Form.Text className="text-muted small">
                        Sum of positive scores
                      </Form.Text>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="small">Total Negative</Form.Label>
                      <Form.Select
                        size="sm"
                        value={formData.scoreStorage.totalNegativeField}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          scoreStorage: { ...prev.scoreStorage, totalNegativeField: e.target.value }
                        }))}
                      >
                        <option value="">-- Select Field --</option>
                        {fields
                          .filter(f => ['number', 'integer', 'currency', 'percent'].includes(f.data_type?.toLowerCase()))
                          .map(field => (
                            <option key={field.field_name} value={field.field_name}>
                              {field.field_label || field.field_name}
                            </option>
                          ))}
                      </Form.Select>
                      <Form.Text className="text-muted small">
                        Sum of negative scores
                      </Form.Text>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="small">Total Score (Net)</Form.Label>
                      <Form.Select
                        size="sm"
                        value={formData.scoreStorage.totalScoreField}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          scoreStorage: { ...prev.scoreStorage, totalScoreField: e.target.value }
                        }))}
                      >
                        <option value="">-- Select Field --</option>
                        {fields
                          .filter(f => ['number', 'integer', 'currency', 'percent'].includes(f.data_type?.toLowerCase()))
                          .map(field => (
                            <option key={field.field_name} value={field.field_name}>
                              {field.field_label || field.field_name}
                            </option>
                          ))}
                      </Form.Select>
                      <Form.Text className="text-muted small">
                        Net score (+ minus -)
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>

          {/* Range-Based Field Updates */}
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>
                <strong>Update Fields Based on Score Ranges *</strong>
                <br />
                <small className="text-muted">(At least one field required)</small>
              </span>
              {formData.object_name && !loadingFields && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleAddRangeBasedUpdate}
                >
                  <FaPlus className="me-1" />
                  Add Field with Ranges
                </Button>
              )}
            </Card.Header>
            <Card.Body>
              {!formData.object_name ? (
                <div className="text-center py-4 text-muted">
                  <FaInfoCircle size={32} className="mb-2" />
                  <p>Select an object first to configure field updates.</p>
                </div>
              ) : formData.rangeBasedUpdates.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <FaInfoCircle size={32} className="mb-2" />
                  <p className="small">Update picklist or text fields based on score ranges</p>
                  <p className="small text-muted">E.g., Rating field: 0-50=Cold, 51-79=Warm, 80-100=Hot</p>
                  <Button
                    variant="primary"
                    onClick={handleAddRangeBasedUpdate}
                    className="mt-2"
                  >
                    <FaPlus className="me-1" />
                    Add Your First Field
                  </Button>
                </div>
              ) : (
                <ListGroup>
                  {formData.rangeBasedUpdates.map((update, updateIndex) => {
                    const selectedField = getFieldByName(update.field);

                    return (
                      <ListGroup.Item key={updateIndex} className="mb-3">
                        <Row className="align-items-center mb-3">
                          <Col md={1}>
                            <Badge bg="primary">#{updateIndex + 1}</Badge>
                          </Col>
                          <Col md={9}>
                            <Form.Label className="small mb-1">Select Field</Form.Label>
                            <Form.Select
                              size="sm"
                              value={update.field}
                              onChange={(e) => handleRangeBasedUpdateFieldChange(updateIndex, e.target.value)}
                              required
                            >
                              <option value="">-- Select Field --</option>
                              {fields.map(field => (
                                <option key={field.field_name} value={field.field_name}>
                                  {field.field_label || field.field_name} ({field.data_type})
                                </option>
                              ))}
                            </Form.Select>
                          </Col>
                          <Col md={2}>
                            <Form.Label className="small mb-1">&nbsp;</Form.Label>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleRemoveRangeBasedUpdate(updateIndex)}
                              className="w-100"
                            >
                              <FaTrash />
                            </Button>
                          </Col>
                        </Row>

                        {/* Score Ranges */}
                        {update.field && (
                          <div className="ps-4 border-start border-3 border-info">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <small className="text-muted fw-bold">Score Ranges:</small>
                              <Button
                                variant="outline-success"
                                size="sm"
                                onClick={() => handleAddRange(updateIndex)}
                              >
                                <FaPlus className="me-1" />
                                Add Range
                              </Button>
                            </div>

                            {update.ranges && update.ranges.length > 0 ? (
                              update.ranges.map((range, rangeIndex) => (
                                <Row key={rangeIndex} className="align-items-center mb-2 ms-2">
                                  <Col md={3}>
                                    <Form.Label className="small mb-1">From Score</Form.Label>
                                    <Form.Control
                                      size="sm"
                                      type="number"
                                      value={range.minScore === 0 ? 0 : (range.minScore || '')}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        handleRangeChange(updateIndex, rangeIndex, 'minScore', val === '' ? '' : parseInt(val));
                                      }}
                                      placeholder="From"
                                    />
                                  </Col>
                                  <Col md={3}>
                                    <Form.Label className="small mb-1">To Score</Form.Label>
                                    <Form.Control
                                      size="sm"
                                      type="number"
                                      value={range.maxScore === 0 ? 0 : (range.maxScore || '')}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        handleRangeChange(updateIndex, rangeIndex, 'maxScore', val === '' ? '' : parseInt(val));
                                      }}
                                      placeholder="To"
                                    />
                                  </Col>
                                  <Col md={5}>
                                    <Form.Label className="small mb-1">Set Field Value</Form.Label>
                                    {isPicklistField(update.field) ? (
                                      <Form.Select
                                        size="sm"
                                        value={range.value}
                                        onChange={(e) => handleRangeChange(updateIndex, rangeIndex, 'value', e.target.value)}
                                      >
                                        <option value="">-- Select Value --</option>
                                        {selectedField?.picklist_values?.map(pv => (
                                          <option key={pv.value_key} value={pv.value_key}>
                                            {pv.value_label || pv.value_key}
                                          </option>
                                        ))}
                                      </Form.Select>
                                    ) : (
                                      <Form.Control
                                        size="sm"
                                        type="text"
                                        value={range.value}
                                        onChange={(e) => handleRangeChange(updateIndex, rangeIndex, 'value', e.target.value)}
                                        placeholder="Value to set"
                                      />
                                    )}
                                  </Col>
                                  <Col md={1}>
                                    <Form.Label className="small mb-1">&nbsp;</Form.Label>
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() => handleRemoveRange(updateIndex, rangeIndex)}
                                      className="w-100"
                                    >
                                      <FaTrash />
                                    </Button>
                                  </Col>
                                </Row>
                              ))
                            ) : (
                              <div className="text-center py-3 text-muted small">
                                No ranges added. Click "Add Range" to start.
                              </div>
                            )}
                          </div>
                        )}
                      </ListGroup.Item>
                    );
                  })}
                </ListGroup>
              )}
            </Card.Body>
          </Card>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={saving}>
            <FaTimes className="me-1" />
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              <>
                <FaSave className="me-1" />
                {editingRule ? 'Update Rule' : 'Create Rule'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ScoringRuleForm;
